import React from 'react'
import { Link } from 'react-router-dom'
import ContactForm from './ContactForm'
import SEO from './SEO'
import { trackCtaClick } from '../utils/analytics'
import './../styles/ServicePage.css'

/**
 * SplitSection
 * Editorial alternating split — large gradient number + heading on one side,
 * paragraph on the other. No card / no box, just type, light dividers and
 * accent gradients. The `align` prop controls whether the heading sits on the
 * left or right.
 */
export const SplitSection = ({ index, align = 'left', kicker, title, children, accent }) => (
  <section className={`sp-split sp-split--${align}`} aria-labelledby={`sp-${index}-h`}>
    <div className="sp-split__inner">
      <div className="sp-split__heading">
        <span className="sp-split__num" aria-hidden="true">
          {String(index).padStart(2, '0')}
        </span>
        {kicker && <span className="sp-split__kicker">{kicker}</span>}
        <h2 id={`sp-${index}-h`} className="sp-split__title">
          {title}
        </h2>
        {accent && <div className="sp-split__accent" aria-hidden="true">{accent}</div>}
      </div>
      <div className="sp-split__body">
        {children}
      </div>
    </div>
    <span className="sp-split__rule" aria-hidden="true" />
  </section>
)

/**
 * StackRibbon — horizontal flowing pill row of tools/technologies.
 * Not a tile/box — simple inline chips.
 */
export const StackRibbon = ({ label, items }) => (
  <section className="sp-stack" aria-label={label}>
    <div className="sp-stack__inner">
      <h3 className="sp-stack__label">{label}</h3>
      <ul className="sp-stack__list">
        {items.map((item) => (
          <li key={item} className="sp-stack__item">{item}</li>
        ))}
      </ul>
    </div>
  </section>
)

/**
 * ServicePage — wraps a service detail page in:
 *  - SEO Helmet (title, description, keywords, canonical, OG, Twitter, JSON-LD)
 *  - hero (gradient title + sub + CTA + animated SVG visual)
 *  - children (alternating SplitSections, StackRibbon, etc.)
 *  - bottom CTA + ContactForm
 */
const ServicePage = ({
  slug,
  seo,
  hero,
  visual,
  children,
}) => {
  const path = `/services/${slug}`

  return (
    <>
      <SEO
        path={path}
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Service',
          serviceType: seo.title,
          provider: {
            '@type': 'Organization',
            name: 'QA Tech',
            url: 'https://qatechltd.com',
          },
          areaServed: 'Worldwide',
          description: seo.description,
          url: `https://qatechltd.com${path}`,
        }}
      />

      <div className="body sp-body">
        <header className="sp-hero">
          <div className="sp-hero__bg" aria-hidden="true">
            <span className="sp-hero__orb sp-hero__orb--a" />
            <span className="sp-hero__orb sp-hero__orb--b" />
            <span className="sp-hero__grid" />
          </div>
          <div className="sp-hero__inner">
            <nav className="sp-breadcrumbs" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span aria-hidden="true">/</span>
              <span>Services</span>
              <span aria-hidden="true">/</span>
              <span>{hero.title}</span>
            </nav>

            <div className="sp-hero__content">
              <span className="sp-hero__eyebrow">{hero.eyebrow}</span>
              <h1 className="sp-hero__title">{hero.title}</h1>
              <p className="sp-hero__lede">{hero.lede}</p>
              <div className="sp-hero__cta-row">
                <a
                  href="#sp-contact"
                  className="sp-cta-primary"
                  onClick={() => trackCtaClick('Start a conversation', `service_${slug}`)}
                >
                  Start a conversation
                </a>
                <Link to="/" className="sp-cta-secondary">Back to all services &rarr;</Link>
              </div>
            </div>

            {visual && <div className="sp-hero__visual" aria-hidden="true">{visual}</div>}
          </div>
        </header>

        <main className="sp-main">{children}</main>

        <section className="sp-cta-section">
          <div className="sp-cta-section__inner">
            <h2>Let&rsquo;s build something your customers will trust.</h2>
            <p>
              Tell us about your project &mdash; we&rsquo;ll come back with a clear,
              practical plan and a fair quote.
            </p>
          </div>
        </section>

        <div id="sp-contact">
          <ContactForm />
        </div>
      </div>
    </>
  )
}

export default ServicePage
